import { probeLabel } from '../../../util/probe-label';

/**
 * Where the response to a sensor's alerts stands, for the dashboard grid's
 * Alert Response column, its filter chips, its sort, the hover card, the
 * sensor flyout and the CSV: one state per sensor, worked out from two
 * sources.
 *
 *   - sensor.arc: the Resolution Center's summary of the sensor (GET
 *     /sensors/arc, relayed from arcsrv), present while something is on its
 *     account's Center list: a card open (who is working on it, who snoozed
 *     it, its thread), an alert being logged, or a card a person closed while
 *     the reading is still out of range.
 *   - sensor.alerts: the enterprise's alerts on the sensor and their streaks
 *     (GET /sensors, the account's own alerts dropped in getSensors() unless
 *     the enterprise follows them: Settings -> Global Alerts), for
 *     what the Center does not track: warning-only alerts, accounts without
 *     the Center, and the calm states.
 *   - sensor.unheard: its Center alerts nobody would hear about (GET
 *     /sensors/arc's unnotified, Incidents::alertsNobodyHears()): no contact
 *     on them is emailed or texted, because the alert's email and text are
 *     off, every contact turned both off or never consented, or nobody is on
 *     it. An incident would open and no one would be told: non-compliance,
 *     said on the row whatever else it says.
 *
 * The words follow the app's Resolution Center (temperaturestick,
 * assets/js/templates/dashboard/arc/): "Sam is on it", "No one has addressed
 * this alert yet", "Snoozed ... until", "Back in range", "Resolved", "False alarm".
 */

// rank: how much a state needs someone (the column sorts on it, most first)
export const RESPONSE_STATES = {
  needs_response: { rank: 90, label: 'Needs response', tone: 'red', icon: 'fas fa-circle' },
  logging:        { rank: 85, label: 'New alert', tone: 'red', icon: 'fas fa-circle' },
  needs_closing:  { rank: 70, label: 'Needs closing', tone: 'amber', icon: 'far fa-undo' },
  snoozed:        { rank: 60, label: 'Snoozed', tone: 'purple', icon: 'far fa-clock' },
  // firing with nobody tracking it: above one someone has in hand
  alerting:       { rank: 55, label: 'Alerting', tone: 'amber', icon: 'fas fa-exclamation-triangle' },
  working:        { rank: 50, label: 'In progress', tone: 'blue', icon: 'fas fa-bolt' },
  // nothing firing, but nobody would be told if it did
  unheard:        { rank: 45, label: 'No alert contacts', tone: 'crimson', icon: 'far fa-bell-slash' },
  closed:         { rank: 30, label: 'Resolved', tone: 'green', icon: 'far fa-check' },
  recent:         { rank: 20, label: 'Recently alerted', tone: 'muted-amber', icon: 'far fa-history' },
  no_alerts:      { rank: 15, label: 'No alerts set', tone: 'grey', icon: 'far fa-ban' },
  clear:          { rank: 10, label: 'All clear', tone: 'green-muted', icon: 'far fa-check' },
};

// The filter chips over the grid, in the order they read: what needs someone
// first. Each counts the sensors in the view it matches (offline is the
// sensor's own state, so it can overlap the others).
export const RESPONSE_CHIPS = [
  { key: 'needs', label: 'Need response', one: 'Needs response', tone: 'red', icon: 'fas fa-circle', states: ['needs_response', 'logging'] },
  // every sensor with an alert nobody would hear about, whatever its state:
  // a setup gap, not a problem in the building, so its count doesn't shout
  // (the sensor's own row keeps its crimson mark)
  { key: 'unheard', label: 'No alert contacts', tone: 'grey', icon: 'far fa-bell-slash', unheard: true },
  { key: 'closing', label: 'Need closing', one: 'Needs closing', tone: 'amber', icon: 'far fa-undo', states: ['needs_closing'] },
  { key: 'working', label: 'In progress', tone: 'blue', icon: 'fas fa-bolt', states: ['working'] },
  { key: 'snoozed', label: 'Snoozed', tone: 'purple', icon: 'far fa-clock', states: ['snoozed'] },
  { key: 'alerting', label: 'Alerting', tone: 'amber', icon: 'fas fa-exclamation-triangle', states: ['alerting'] },
  { key: 'closed', label: 'Resolved', tone: 'green', icon: 'far fa-check', states: ['closed'] },
  { key: 'offline', label: 'Offline', tone: 'grey', icon: 'far fa-wifi-slash', offline: true },
  { key: 'no_alerts', label: 'No alerts set', tone: 'grey', icon: 'far fa-ban', states: ['no_alerts'] },
];

// whether the Center has answered (set by the dashboard on each answer):
// until it has, an alert with no Center row may well be on someone's list,
// so the words don't guess
let centerReachable = false;
export const setCenterReachable = reachable => {
  centerReachable = !!reachable;
};

// the sensor's Center alerts nobody would hear about
export const unheardFor = sensor => (Array.isArray(sensor.unheard) ? sensor.unheard : []);

// why nobody hears one alert ("it") or several ("them")
const UNHEARD_WHY = {
  no_contacts: ['no contacts are on it', 'no contacts are on them'],
  contacts_off: ['its contacts have email and text off, or have not consented', 'their contacts have email and text off, or have not consented'],
  alert_off: ['email and text are switched off on it for every contact', 'email and text are switched off on them for every contact'],
};

/**
 * The sensor's alerts nobody would hear about, said once. It is nearly
 * always one cause for the whole account (nobody on any alert, or every
 * contact opted out), so the alerts are counted, not listed, and grouped by
 * why: {count, all (every alert on the sensor), scope ("All 6 alerts", "2
 * alerts", or the one alert's name), groups [{reason, alerts, why}], push
 * (app push on for any of them)}. Null when there are none.
 */
export const unheardSummary = sensor => {
  const unheard = unheardFor(sensor);
  if (!unheard.length) return null;

  const count = unheard.length;
  const total = Array.isArray(sensor.alerts) ? sensor.alerts.length : count;
  const all = count >= total;

  const groups = Object.keys(UNHEARD_WHY)
    .map(reason => ({ reason, alerts: unheard.filter(u => u.reason === reason) }))
    .filter(group => group.alerts.length);
  groups.forEach(group => {
    group.why = UNHEARD_WHY[group.reason][group.alerts.length === 1 ? 0 : 1];
  });

  return {
    count,
    all,
    scope: count === 1 ? unheard[0].alert_name : (all ? `All ${count} alerts` : `${count} alerts`),
    groups,
    push: unheard.some(u => u.push),
  };
};

// "Temp Level Urgent", "Temp Level Urgent or Freeze watch", "A, B or 3 more"
const alertNames = alerts => {
  const names = alerts.map(a => `<b>${esc(a.alert_name)}</b>`);
  if (names.length <= 2) return names.join(' or ');
  return `${names.slice(0, 2).join(', ')} or ${names.length - 2} more`;
};

// "+6 other alerts": the rest of a Resolution Center card's alerts, the app
// card's words (a bare "7 alerts" reads as one alert triggered 7 times)
const otherAlerts = count => `+${count - 1} other ${count - 1 === 1 ? 'alert' : 'alerts'}`;

export const chipMatches = (chip, sensor, response) => {
  if (chip.offline) return sensor.offline == 1;
  if (chip.unheard) return unheardFor(sensor).length > 0;
  return chip.states.includes(response.state);
};

const esc = value => {
  const v = String(value ?? '');
  if (window.escapeHTML) return window.escapeHTML(v);
  return v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
};

// "2026-10-05 14:03:00" (UTC, the services' format), with or without a Z
const toDate = value => {
  if (!value) return null;
  const d = window.datetimeToDate(String(value));
  return d && !isNaN(d) ? d : null;
};

// seconds until a card back in range closes by itself (arc.auto_close_at:
// the enterprise let every alert on it do that), null when it waits for a
// person
const autoCloseLeft = (arc, now) => {
  const at = toDate(arc && arc.auto_close_at);
  return at ? Math.round((at - now) / 1000) : null;
};

const secondsSince = (value, now) => {
  const d = toDate(value);
  return d ? Math.max(0, Math.round((now - d) / 1000)) : null;
};

/** "45s", "12m", "2h 14m", "3d 4h" */
export const shortDuration = seconds => {
  if (seconds == null) return '';
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return m % 60 ? `${h}h ${m % 60}m` : `${h}h`;
  const d = Math.floor(h / 24);
  return h % 24 ? `${d}d ${h % 24}h` : `${d}d`;
};

const clock = value => {
  const d = toDate(value);
  if (!d) return '';
  const m = window.getUserTimezoneDate(String(value));
  const sameDay = new Date().toDateString() === d.toDateString();
  return sameDay ? m.format('h:mm a') : m.format('MMM D, h:mm a');
};

// "Sam Dolen" -> "SD"; "sam" -> "S"
export const initials = name => {
  const parts = String(name || '').replace(/\(.*\)/, '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
};

// "Sam Dolen" -> "Sam D."
const shortName = name => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'Someone';
  return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : parts[0];
};

const READING_NAMES = {
  temperature: () => 'Temperature',
  probe_temp: () => probeLabel(),
  humidity: () => 'Humidity',
  power: () => 'Power',
  battery: () => 'Battery',
  connection: () => 'Connection',
  dew_point: () => 'Dew point',
  heat_index: () => 'Heat index',
};

/**
 * A value as the Center words it ("74.3 F", "33 %") in the viewer's unit:
 * the Center writes the account's own unit, which need not be the viewer's.
 */
export const formatArcValue = value => {
  const text = String(value ?? '').trim();
  const m = text.match(/^(-?\d+(?:\.\d+)?)\s*°?\s*([FC])$/i);
  if (!m) return text;
  let n = parseFloat(m[1]);
  const from = m[2].toUpperCase();
  const to = window.getUserTempPref ? window.getUserTempPref() : 'F';
  if (from !== to) {
    n = to === 'F' ? n * 9 / 5 + 32 : (n - 32) * 5 / 9;
  }
  return `${Math.round(n * 10) / 10}°${to}`;
};

const conditionText = arc => {
  const name = (READING_NAMES[arc.reading_type] || (() => ''))();
  const parts = [];
  if (name && arc.reading_condition) {
    parts.push(`${name} ${arc.reading_condition}${arc.alert_value ? ' ' + formatArcValue(arc.alert_value) : ''}`);
  }
  if (arc.current_value && arc.condition_active) {
    parts.push(`now ${formatArcValue(arc.current_value)}`);
  }
  return parts.join(' · ');
};

/** the firing alert the sensor's own alerts say is worst (no Center row) */
const activeAlerts = sensor => {
  const alerts = Array.isArray(sensor.alerts) ? sensor.alerts : [];
  const firing = alerts.filter(a => parseInt(a.streak, 10) > 0);
  const recent = alerts.filter(a => {
    if (parseInt(a.streak, 10) > 0 || !a.last_triggered) return false;
    const since = secondsSince(a.last_triggered, Date.now());
    return since != null && since < (parseInt(sensor.send_interval, 10) || 3600) * 4;
  });
  return { alerts, firing, recent };
};

/**
 * The sensor's response state: {state, rank, label, tone, icon, alertName,
 * since (seconds), people..., arc}. `now` is a Date.now() value.
 */
export const responseFor = (sensor, now = Date.now()) => {

  const arc = sensor.arc || null;
  const base = key => Object.assign({ state: key, arc, sensor }, RESPONSE_STATES[key]);

  if (arc) {
    const working = Array.isArray(arc.working) ? arc.working : [];
    const snoozed = (Array.isArray(arc.snoozed) ? arc.snoozed : [])
      .filter(s => { const d = toDate(s.until); return d && d > now; });
    const since = secondsSince(arc.started_at, now);
    const common = { alertName: arc.alert_name || '', since, working, snoozed, comments: +arc.comments || 0 };

    if (arc.kind === 'pending') {
      return Object.assign(base('logging'), common);
    }

    if (arc.kind === 'closed') {
      const r = base('closed');
      if (arc.status === 'false_alarm') {
        r.label = 'False alarm';
        r.icon = 'far fa-times';
      } else if (arc.status === 'auto_resolved') {
        r.label = 'Closed';
      }
      return Object.assign(r, common, { closedAgo: secondsSince(arc.closed_at, now) });
    }

    // open: someone on it beats a snooze beats nobody
    if (working.length) {
      const r = Object.assign(base('working'), common);
      r.label = working.length > 1
        ? `${shortName(working[0].name)} +${working.length - 1} on it`
        : `${shortName(working[0].name)} is on it`;
      return r;
    }

    if (snoozed.length) {
      const r = Object.assign(base('snoozed'), common);
      const last = snoozed.reduce((a, b) => ((toDate(a.until) > toDate(b.until)) ? a : b));
      r.snoozeLeft = Math.round((toDate(last.until) - now) / 1000);
      r.label = `Snoozed · ${shortDuration(r.snoozeLeft)} left`;
      return r;
    }

    if (!arc.condition_active) {
      // back in range: a card that needs a cause waits for someone to close it
      // -- or, the enterprise having let its alert close by itself, counts
      // down (still someone's to close before then)
      const r = Object.assign(base('needs_closing'), common);
      const left = autoCloseLeft(arc, now);
      if (!arc.requires_resolution) {
        r.label = 'Back in range';
      } else if (left != null) {
        r.label = `Closes in ${shortDuration(Math.max(left, 60))}`;
        r.autoCloseLeft = left;
      }
      return r;
    }

    return Object.assign(base('needs_response'), common);
  }

  const { alerts, firing, recent } = activeAlerts(sensor);

  if (firing.length) {
    const worst = firing.slice().sort((a, b) => String(a.last_triggered).localeCompare(String(b.last_triggered)))[0];
    return Object.assign(base('alerting'), {
      alertName: worst.alert_name || '', since: secondsSince(worst.last_triggered, now), firing,
    });
  }

  if (recent.length && !unheardFor(sensor).length) {
    const last = recent.slice().sort((a, b) => String(b.last_triggered).localeCompare(String(a.last_triggered)))[0];
    return Object.assign(base('recent'), { alertName: last.alert_name || '', since: secondsSince(last.last_triggered, now) });
  }

  if (!alerts.length) {
    return base('no_alerts');
  }

  // calm, but if anything did fire nobody would be told
  const unheard = unheardSummary(sensor);
  if (unheard) {
    return Object.assign(base('unheard'), { unheard, alertName: unheard.scope });
  }

  return base('clear');
};

/**
 * The sort value: the state's rank, then within it the longest waiting first
 * (the column sorts descending to put what needs someone on top).
 */
export const responseSortValue = (sensor, now = Date.now()) => {
  const r = responseFor(sensor, now);
  return r.rank * 1e9 + Math.min(r.since || 0, 1e9 - 1);
};

// ---- the grid cell --------------------------------------------------------------

const avatar = person => `<span class="arc-avatar" aria-hidden="true">${esc(initials(person.name))}</span>`;

const pill = (r, text) => `
  <span class="arc-pill arc-pill--${r.tone}">
    ${r.icon ? `<i class="${r.icon}" aria-hidden="true"></i>` : ''}<span class="arc-pill__text">${esc(text)}</span>
  </span>`;

/** The Alert Response cell: a pill, then the alert and how long. */
export const renderResponseCell = sensor => {
  const r = responseFor(sensor);

  if (r.state === 'clear' || r.state === 'no_alerts') {
    return `<span class="arc-cell arc-cell--quiet" data-response="${r.state}">${r.icon ? `<i class="${r.icon}" aria-hidden="true"></i> ` : ''}${esc(r.label)}</span>`;
  }

  let lead = '';
  let meta = [];

  // how long it has waited comes first: when the column is narrow it is the
  // alert's name that gives way
  switch (r.state) {
    case 'working':
      lead = `${r.working.slice(0, 2).map(avatar).join('')}${pill(r, r.label)}`;
      meta.push(shortDuration(r.since), r.alertName);
      break;
    case 'snoozed':
      lead = pill(r, r.label);
      meta.push(shortName(r.snoozed[0].name), r.alertName);
      break;
    case 'closed':
      lead = pill(r, r.label);
      meta.push(r.closedAgo != null ? `${shortDuration(r.closedAgo)} ago` : '', r.arc.resolved_by_name ? shortName(r.arc.resolved_by_name) : '');
      break;
    case 'recent':
      lead = pill(r, r.label);
      meta.push(r.since != null ? `${shortDuration(r.since)} ago` : '', r.alertName);
      break;
    case 'unheard':
      lead = pill(r, r.label);
      meta.push(r.alertName);
      break;
    default:
      lead = pill(r, r.label);
      meta.push(shortDuration(r.since), r.alertName);
  }

  meta = meta.filter(Boolean);

  // on any state but its own, an alert nobody would hear about: beside the
  // pill, where a narrow column still shows it
  const unheard = unheardSummary(sensor);
  if (unheard && r.state !== 'unheard') {
    const which = unheard.count === 1 ? unheard.scope : (unheard.all ? `any of its ${unheard.count} alerts` : `${unheard.count} of its alerts`);
    lead += `<span class="arc-cell__extra arc-cell__unheard" title="No alert contacts for ${esc(which)}" aria-label="No alert contacts"><i class="far fa-bell-slash" aria-hidden="true"></i></span>`;
  }

  // the thread, and a snooze under someone's claim
  const extras = [];
  if (r.state === 'working' && r.snoozed && r.snoozed.length) {
    extras.push('<i class="far fa-clock arc-cell__extra" title="Also snoozed" aria-label="Also snoozed"></i>');
  }
  if (r.comments) {
    extras.push(`<span class="arc-cell__extra" title="${r.comments} comment${r.comments === 1 ? '' : 's'}"><i class="far fa-comment" aria-hidden="true"></i>${r.comments}</span>`);
  }
  if (r.arc && +r.arc.alerts > 1) {
    extras.push(`<span class="arc-cell__extra" title="${otherAlerts(r.arc.alerts)}">+${r.arc.alerts - 1}</span>`);
  }

  return `
    <span class="arc-cell" data-response="${r.state}" data-sensor="${esc(sensor.sensor_id)}">
      ${lead}
      ${meta.length ? `<span class="arc-cell__meta">${esc(meta.join(' · '))}</span>` : ''}
      ${extras.join('')}
    </span>`;
};

/** the CSV's words for the state */
export const responseText = sensor => {
  const r = responseFor(sensor);
  // the state's own name, not the cell's short line ("Sam D. is on it")
  const bits = [['working', 'snoozed'].includes(r.state) ? RESPONSE_STATES[r.state].label : r.label];
  if (r.alertName) bits.push(r.alertName);
  if (r.state === 'working') bits.push(r.working.map(p => p.name).join(', '));
  if (r.state === 'snoozed') bits.push(`by ${r.snoozed.map(p => p.name).join(', ')}`);
  if (r.state === 'closed' && r.arc.resolved_by_name) bits.push(`by ${r.arc.resolved_by_name}`);
  // "No alert contacts - All 6 alerts - no contacts are on them"
  const unheard = unheardSummary(sensor);
  if (unheard) {
    // several causes: each says which alerts it is about
    const why = unheard.groups.length === 1
      ? unheard.groups[0].why
      : unheard.groups.map(g => `${g.alerts.map(a => a.alert_name).join(', ')}: ${g.why}`).join('; ');
    bits.push(r.state === 'unheard' ? why : `No alert contacts (${unheard.scope}): ${why}`);
  }
  return bits.filter(Boolean).join(' - ');
};

// ---- the details: the hover card and the sensor flyout ----------------------------

/**
 * Everything the summary says, as lines: what fired and when, who is on it,
 * snoozes, who has seen it, the thread, the close.
 */
export const renderResponseDetails = sensor => {
  const r = responseFor(sensor);
  const arc = r.arc;
  const now = Date.now();
  const lines = [];

  const line = (icon, html, cls = '') => lines.push(`
    <li class="arc-details__line ${cls}">
      <i class="${icon}" aria-hidden="true"></i>
      <span>${html}</span>
    </li>`);

  if (arc) {
    const cond = conditionText(arc);
    if (cond) line('far fa-thermometer-half', esc(cond));

    if (arc.started_at) {
      line('far fa-bell', `Triggered <b>${esc(shortDuration(r.since))} ago</b> <span class="arc-details__muted">${esc(clock(arc.started_at))}</span>`);
    }

    if (arc.kind === 'pending') {
      line('far fa-hourglass-half', 'Being logged for the team now');
    }

    if (arc.kind === 'open') {
      if (r.working.length) {
        r.working.forEach(p => line('fas fa-bolt', `<b>${esc(p.name || 'Someone')}</b> is working on it <span class="arc-details__muted">since ${esc(clock(p.since))}</span>`, 'is-working'));
      } else {
        line('far fa-user-clock', 'No one has addressed this alert yet', 'is-alarm');
      }

      r.snoozed.forEach(s => {
        const left = Math.round((toDate(s.until) - now) / 1000);
        line('far fa-clock', `<b>${esc(s.name || 'Someone')}</b> snoozed it <span class="arc-details__muted">until ${esc(clock(s.until))} · ${esc(shortDuration(left))} left</span>`, 'is-snoozed');
      });

      if (!arc.condition_active) {
        const left = autoCloseLeft(arc, now);
        line('far fa-undo', !arc.requires_resolution
          ? 'Back in range · closes on its own'
          : (left != null
            ? `Back in range · closes on its own in ${shortDuration(Math.max(left, 60))} if it stays in range, unless someone resolves it first`
            : 'Back in range · waiting for someone to resolve it or mark it a false alarm'));
      }
    }

    if (arc.kind === 'closed') {
      const who = arc.resolved_by_name ? ` by <b>${esc(arc.resolved_by_name)}</b>` : '';
      const what = arc.status === 'false_alarm' ? 'Marked a false alarm' : (arc.status === 'auto_resolved' ? 'Closed' : 'Resolved');
      line(arc.status === 'false_alarm' ? 'far fa-times' : 'far fa-check', `${what}${who} <span class="arc-details__muted">${esc(shortDuration(r.closedAgo))} ago</span>`, 'is-closed');
      if (arc.resolution_reason) {
        line('far fa-quote-left', `“${esc(arc.resolution_reason)}”`);
      }
      line('far fa-exclamation-circle', `Still out of range${arc.listed_until ? ` · listed until ${esc(clock(arc.listed_until))}` : ''}`);
    }

    // nobody was sent it (said once: a setup nobody hears is said below)
    if (arc.kind === 'open' && !(+arc.sent_to > 0) &&
      !unheardFor(sensor).some(u => String(u.alert_id) === String(arc.alert_id))) {
      line('far fa-bell-slash', '<b>No contact was notified</b> of this alert', 'is-unheard');
    }

    if (+arc.sent_to > 0) {
      line('far fa-eye', `Seen by ${+arc.seen || 0} of ${+arc.sent_to} notified`);
    }

    if (arc.latest_comment) {
      const c = arc.latest_comment;
      line('far fa-comment', `${r.comments > 1 ? `${r.comments} comments · latest ` : ''}<b>${esc(c.name || 'Someone')}</b>: “${esc(c.message)}” <span class="arc-details__muted">${esc(shortDuration(secondsSince(c.created, now)))} ago</span>`);
    } else if (r.comments) {
      line('far fa-comment', `${r.comments} comment${r.comments === 1 ? '' : 's'}`);
    }

    if (+arc.alerts > 1) {
      line('far fa-layer-group', otherAlerts(arc.alerts));
    }
  } else if (r.state === 'alerting') {
    r.firing.forEach(a => line('fas fa-exclamation-triangle', `<b>${esc(a.alert_name || 'Alert')}</b> since ${esc(clock(a.last_triggered))} <span class="arc-details__muted">${esc(shortDuration(secondsSince(a.last_triggered, now)))}</span>`));
    line('far fa-info-circle', centerReachable
      ? 'Not tracked in the Resolution Center (a warning-only alert, or the account is not in the Center)'
      : 'The Resolution Center has not answered: who is on it is not known right now', 'arc-details__muted');
  } else if (r.state === 'recent') {
    line('far fa-history', `<b>${esc(r.alertName || 'An alert')}</b> triggered ${esc(shortDuration(r.since))} ago and is back to normal`);
  } else if (r.state === 'no_alerts') {
    line('far fa-bell-slash', 'No alerts are set on this sensor: nobody is told if something goes wrong');
  } else if (r.state !== 'unheard') {
    const count = (sensor.alerts || []).length;
    line('far fa-check', `${count} alert${count === 1 ? '' : 's'} watching, none triggered`);
  }

  // one line per cause (nearly always one), the push note once
  const unheard = unheardSummary(sensor);
  if (unheard) {
    unheard.groups.forEach(group => {
      const when = unheard.all && unheard.groups.length === 1 && unheard.count > 1
        ? `any of its ${unheard.count} alerts`
        : alertNames(group.alerts);
      line('far fa-bell-slash', `<b>No alert contacts</b> for ${when}: ${esc(group.why)}`, 'is-unheard');
    });
    if (unheard.push) {
      line('far fa-mobile-alt', 'App push is on, but only reaches people with the app', 'arc-details__muted');
    }
    if (r.state === 'unheard') {
      line('far fa-check', 'Nothing is triggered right now', 'arc-details__muted');
    }
  }

  if (sensor.offline == 1 && sensor.last_checkin) {
    line('far fa-wifi-slash', `Offline · last reading ${esc(shortDuration(secondsSince(sensor.last_checkin, now)))} ago`, 'is-alarm');
  }

  return `
    <div class="arc-details" data-response="${r.state}">
      <div class="arc-details__head">
        ${pill(r, r.state === 'working' || r.state === 'snoozed' ? RESPONSE_STATES[r.state].label : r.label)}
        ${r.alertName ? `<span class="arc-details__alert">${esc(r.alertName)}</span>` : ''}
      </div>
      <ul class="arc-details__lines">${lines.join('')}</ul>
    </div>`;
};
